import { Component, OnInit, Input, HostListener } from '@angular/core';
import { formatDate } from '@angular/common';
import { ClientsService } from '../../../shared/services/clients.service';
import { ClientContactsService } from '../../services/client-contacts.service';
import { AccountDetails, ClientInformationInterface } from '../../../shared/interfaces/client-data';
import {
    GridComponent,
    GridDataResult,
    PageChangeEvent,
    CellClickEvent,
} from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy, filterBy } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { forkJoin } from 'rxjs';
import { IntegrationSourceService } from '../../services/integration-source.service';
import {
    NoDataTypes,
    NoDataActionType,
    NoDataSourceType,
} from 'app/modules/shared/interfaces/no-data';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';

@Component({
    selector: 'app-lba-clients',
    templateUrl: './lba-clients.component.html',
    styleUrls: ['./lba-clients.component.scss'],
})
export class LbaClientsComponent implements OnInit {
    @Input() events: Observable<void>;
    gridView: GridDataResult;
    pageSize = 10;
    skip = 0;
    isSideViewOpen: boolean = false;
    type: 'numeric' | 'input' = 'input';
    quickview: boolean;
    recordView: boolean;
    action: 'Edit' | 'Add' | 'View';
    addEditOrViewOnly: string;
    isLBA: boolean;
    clientSort: SortDescriptor[];
    clientSearchTerm: string;
    selectedClient: AccountDetails;
    selectedClientFullModel: ClientInformationInterface;
    createClientView: boolean;
    clients: AccountDetails[];
    loading: boolean;
    noDataType: NoDataTypes;
    noClients: boolean;
    exportFileName: string;
    exportData: AccountDetails[];

    constructor(
        private _clientsService: ClientsService,
        private currentUserStateService: CurrentUserStateService,
        private _clientContactService: ClientContactsService,
        private _reusableFunctions: ReusableFunctions,
        private _clientIntegrationService: IntegrationSourceService
    ) {
        this.getExcelExportData = this.getExcelExportData.bind(this);
    }
    ngOnInit() {
        scroll(0, 0);
        this.selectedClientFullModel = {} as ClientInformationInterface;
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'Clients',
            inputSource: NoDataSourceType.table,
        };
        this.clientSort = [{ field: 'clientFullname', dir: 'asc' }];
        this.quickview = false;
        this.recordView = false;
        this.addEditOrViewOnly = 'ViewOnly';
        if (this.currentUserStateService.currentUser.clientShortName == 'LBA') this.isLBA = true;
        else this.isLBA = false;
        this.loadClients();
    }

    // get basic account details for clients
    loadClients() {
        this.loading = true;
        this._clientsService.getAllClientAccountDetails().subscribe(
            (data) => {
                if (data && data.length) {
                    this.clients = data.map((client: AccountDetails) => {
                        return { ...client, activeDate: client.contractDate };
                    });
                    this.loadClientsIntoGridView();
                } else {
                    this.noClients = true;
                }
                this.loading = false;
            },
            (error) => {
                this.loading = false;
                throw new Error(error);
            }
        );
    }

    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 27) {
            //keyCode 27 is the escape key.
            if (this.quickview) {
                this.quickview = false;
                this.isSideViewOpen = false;
            } else if (this.recordView) {
                this.recordView = false;
                this.isSideViewOpen = false;
            }
            this.addEditOrViewOnly = 'viewOnly';
        }
    }
    clientPageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.loadClientsIntoGridView();
    }
    sortChange(sort: SortDescriptor[]): void {
        this.clientSort = sort;
        this.updateGrid();
    }
    filterClients() {
        if (this.clientSearchTerm && this.clientSearchTerm.length > 0) {
            return filterBy(this.clients, {
                logic: 'or',
                filters: [
                    {
                        field: 'fullName',
                        operator: 'contains',
                        value: this.clientSearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'shortName',
                        operator: 'contains',
                        value: this.clientSearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'subscriptionType',
                        operator: 'contains',
                        value: this.clientSearchTerm,
                        ignoreCase: true,
                    },
                    {
                        field: 'clientKey',
                        operator: 'contains',
                        value: this.clientSearchTerm,
                        ignoreCase: true,
                    },
                ],
            });
        }
        return this.clients;
    }

    loadClientsIntoGridView() {
        this.updateGrid();

        this.exportData = this.clients.map((client) => ({
            ...client,
            formattedGoLiveDate: !client.goLiveDate
                ? ''
                : formatDate(client.goLiveDate, 'MM/dd/yyyy', 'en-US'),
            formattedActiveDate: !client.activeDate
                ? ''
                : formatDate(client.activeDate, 'MM/dd/yyyy', 'en-US'),
            calculatedStatus: this.calculateStatus(
                client.activeDate,
                client.inActiveDate,
                client.isActive
            ),
        }));
    }
    updateGrid() {
        let filteredClients = this.filterClients();
        let orderedData = orderBy(filteredClients, this.clientSort);
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    handleCellClick({ columnIndex, dataItem }: CellClickEvent) {
        if (columnIndex == 7) {
            this.action = 'Edit';
        } else {
            this.action = 'View';
        }
        this.isSideViewOpen = true;
        this.recordClicked(dataItem);
    }
    showClientQuickview() {
        this.quickview = true;
        this.isSideViewOpen = true;
    }
    recordClicked(dataItem: any) {
        this.selectedClient = dataItem;
        let shortName = this.selectedClient.shortName;
        forkJoin(
            this._clientsService.getAccountDetails(shortName),
            this._clientContactService.getContactsByName(shortName),
            this._clientsService.getClientIdentifierRecords(shortName),
            this._clientIntegrationService.getClientIntegrationSourcesByName(shortName)
        ).subscribe(([details, contacts, identifiers, clientIntegrationSources]) => {
            this.selectedClientFullModel.clientDetails = details;
            this.selectedClientFullModel.contacts = contacts;
            this.selectedClientFullModel.identifiers = identifiers;
            this.selectedClientFullModel.clientIntegrationSources = clientIntegrationSources;
            this.showClientQuickview();
        });
    }
    closeClientQuickview() {
        this.quickview = false;
        this.isSideViewOpen = false;
        this.selectedClient = null;
    }
    handleCancelClicked() {
        this.recordView = false;
        this.quickview = false;
        this.createClientView = false;
    }

    addClientHandler() {
        this.quickview = true;
        this.isSideViewOpen = true;
        this.action = 'Add';
    }
    updatePageFromSideBar() {
        this.quickview = false;
        this.isSideViewOpen = false;
        this.loadClients();
    }

    calculateStatus(activeDate: Date, inActiveDate: Date, isActive: boolean) {
        activeDate = this._reusableFunctions.getDateFromDateTimeOffset(activeDate);
        inActiveDate = this._reusableFunctions.getDateFromDateTimeOffset(inActiveDate);
        if (!activeDate) {
            return '';
        } else if (this._reusableFunctions.isDateInTheFuture(activeDate) && isActive) {
            return 'Pending';
        } else if (isActive) {
            return 'Active';
        } else if (!isActive) {
            return 'Inactive';
        } else {
            return '';
        }
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName = 'lg-clients_' + dateTime + '_.xlsx';
        kendoGrid.saveAsExcel();
    }

    getExcelExportData(): ExcelExportData {
        const result: ExcelExportData = {
            data: this.exportData,
            group: [],
        };
        return result;
    }
}
